/**
 * The Home panel — the house, the specialization you practise in it, and the bench you
 * practise on. Checklist items 206-208 (wire the home in, grant it, upgrade
 * it), 236 (choose a specialization) and 237 (craft from that specialization,
 * gated by the house level).
 *
 * ─── Why one panel with three tabs ──────────────────────────────────────────
 * They are one building. The house level gates the recipes, the specialization decides
 * which recipes exist at all, and both numbers are printed on the workshop —
 * so three separate panels would each have to fetch and re-print the other
 * two's state, and would each get a chance to show a different level. One
 * store (`home-state.ts`), one load, one set of numbers.
 *
 * ─── The prompt is a predicate, not an event ────────────────────────────────
 * Item 236 says the choice is prompted "upon upgrading to level 2". Firing a
 * one-shot on the upgrade RESPONSE would show it to whoever happened to have
 * the panel open at that moment and to nobody else — not on reload, not on
 * remount, not to a player who bought the home with the Gold key while logged
 * out. `shouldPromptForSpecialization` is a predicate over loaded state, and
 * `openHomeUI` opens straight onto the Specialization tab whenever it holds.
 *
 * ─── Panel conventions this follows ─────────────────────────────────────────
 * Modelled on `shop-ui.ts`/`vendor-ui.ts`: a full-screen scrim at z-index 1001
 * (`.bv-hud` is 1002 and stays readable above it), backdrop-click and Escape to
 * close, and `registerPanel` so the world's input gates pick it up without
 * `index.ts` growing another hand-copied predicate.
 *
 * Styling is entirely `bv-home-*` classes in `styles/home.css.ts`, not inline
 * `cssText`. That keeps every token name inside `BASE_VIEW_CSS`, which
 * `styles.tokens.test.ts` already audits — an undeclared `var(--x)` silently
 * drops its whole declaration, and the audit is the only thing that catches it.
 */
import { CharacterPreview } from './character-preview';
import { pictureHtml } from './paperdoll';
import { closeOtherModals, registerPanel } from './panels';
import { gameStateStore } from '../../state/game-state';
import {
  chooseSpecialization, collectCraft, homeStateStore, inspectSpecialization, loadHome,
  refreshHomeView, resetHomePanelState, setChangingSpec, setHomeTab, setSkipConfirm,
  setUpgradeConfirm, shouldPromptForSpecialization, skipCraft, startCraft, upgradeHome,
} from '../../state/home-state';
import type {
  CraftBench, CraftRecipeView, HomeMaterialLine, HomeState, HomeView,
  SpecializationCatalog, SpecializationOption, ZoneLockReason,
} from '../../state/home-state.types';
import { createCloseButton } from './close-button';

const PANEL_ID = 'home';
const ROOT_ID = 'home-ui';

/**
 * The `npcId` the settlement map's `player-home` building carries.
 *
 * Item 208's entry point is the house itself — "Go to home, press on it" — and
 * the object is already `interactable: true`. Without this route, E on your own
 * house calls `openNpcDialog('home')`, which finds no dialog tree (there are
 * nine seeded and none of them is the house), returns nothing, and does
 * NOTHING AT ALL: no error, no panel, no prompt. `home-ui.test.ts` asserts the
 * map still carries this id on an interactable body, so renaming it in the
 * editor reddens a test rather than silently unbinding the front door.
 */
export const HOME_NPC_ID = 'home';

/**
 * One glyph per vocation, 24x24, stroke-only, `currentColor` — the same
 * construction as `action-bar.ts`'s icons and for the same reason: five glyphs
 * are not worth an asset pipeline, and generated ones stay testable as strings.
 *
 * Keyed by `SpecializationCard.iconKey`, which the server sends. A vocation
 * whose key has no glyph here falls back to a plain square rather than
 * rendering an empty tile.
 */
export const SPEC_ICONS: Record<string, string> = {
  /** A hooked salvage bar over a heap — what you carry out and what you found it in. */
  scavenging: '<path d="M7.4 3.6v7.2a3 3 0 0 0 3 3h.6"/><path d="M5.2 3.6h4.4"/>'
    + '<path d="M3.4 20.4h17.2"/><path d="M6.6 20.4 9 15.6h6l2.4 4.8"/>',
  /** A ring sight with a notch at each quarter — the board points, you go. */
  bounty: '<circle cx="12" cy="12" r="6.4"/><circle cx="12" cy="12" r="1.6"/>'
    + '<path d="M12 3v2.6"/><path d="M12 18.4V21"/><path d="M3 12h2.6"/><path d="M18.4 12H21"/>',
  /** A wrench crossing a bolt head. */
  engineering: '<path d="M14.6 4.6a3.6 3.6 0 0 0 4.6 4.6l-8.8 8.8a2.2 2.2 0 0 1-3.1-3.1Z"/>'
    + '<path d="m5.4 18.6.6.6"/><path d="M16.4 14.2 20 17.8"/><path d="m17.8 12.8-1.4 1.4"/>',
  /** A cook pot with a rising lid and two handles. */
  cooking: '<path d="M4.8 9.6h14.4v5.2a4 4 0 0 1-4 4H8.8a4 4 0 0 1-4-4Z"/>'
    + '<path d="M3 11.4h1.8"/><path d="M19.2 11.4H21"/><path d="M8.6 6.6V4.8"/><path d="M15.4 6.6V4.8"/>',
  /** A needle drawing thread through a seam. */
  tailoring: '<path d="M4.4 19.6 16.8 7.2"/><path d="m15.2 5.6 3.2 3.2"/>'
    + '<path d="M17 3.8 20.2 7"/><path d="M4.6 13.4c2.4 0 2.4 2.4 4.8 2.4"/>',
};

/** Where the painted emblems live (generated with Higgsfield; see assets/generated_items/specializations). */
const SPEC_ART_DIR = '/icons/specializations';

/**
 * The emblem for a vocation: the painted image when the key is one we ship art for, else the
 * stroke glyph, so an unknown key still draws something. `small` picks the 128px tier.
 */
export function specArtHtml(iconKey: string, small = false): string {
  if (!(iconKey in SPEC_ICONS)) return specIconSvg(iconKey);
  return `<img class="bv-home-spec-art" src="${SPEC_ART_DIR}/${iconKey}${small ? '_128' : ''}.webp" alt="" draggable="false" decoding="async">`;
}

/** A glyph as a complete `<svg>` string. Pure — testable without a DOM. */
export function specIconSvg(iconKey: string): string {
  const body = SPEC_ICONS[iconKey] ?? '<rect x="5" y="5" width="14" height="14" rx="2"/>';
  return '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"'
    + ` stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${body}</svg>`;
}

/**
 * What to say when there is no specialization yet, per reason.
 *
 * There is no level or Elder gate any more: the player chooses (and changes)
 * their own specialization. `HOME_NOT_OWNED` only exists for completeness —
 * the panel itself refuses to open without the home.
 */
export const ZONE_LOCK_COPY: Record<ZoneLockReason, (requiredLevel: number) => string> = {
  HOME_NOT_OWNED: () => 'You need a house of your own before you can choose a specialization.',
  NOT_CHOSEN: () => 'Choose the specialization you want to be known for. You can change it later — but you start that one over.',
};

/** Attribute codes as the gate lines name them. */
const ATTRIBUTE_NAMES: Record<string, string> = {
  STR: 'Strength', INT: 'Intellect', STA: 'Stamina', AGI: 'Agility', PER: 'Perception',
};

const PERK_HEADINGS: Record<string, string> = {
  BUFF: 'Passive', CAPABILITY: 'Lets you', SIGNATURE: 'Mastery',
};

const TABS: { id: HomeState['tab']; label: string }[] = [
  { id: 'HOUSE', label: 'The House' },
  { id: 'WORKSHOP', label: 'Workshop' },
];

let unsubscribers: Array<() => void> = [];
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;
let unregisterPanel: (() => void) | null = null;
let preview: CharacterPreview | null = null;

/**
 * The survivor preview's host element, created ONCE and reused.
 *
 * It cannot live in the markup `render()` writes. `render` replaces
 * `body.innerHTML` on every store push, which destroys the host — and
 * `CharacterPreview.build()` runs only on the FIRST `setActive(true)`, so the
 * canvas it appended is never re-attached to the replacement. The result is a
 * preview that renders for one frame and is an empty box from the second store
 * push onward. Found by looking at it; no test had an opinion, because the
 * canvas is created by a component this file stubs out.
 *
 * So the host is persistent and `render()` writes an empty SLOT for it, which
 * `mountPreview` moves it into after each rewrite.
 */
let previewHost: HTMLDivElement | null = null;
/** The window title, OUTSIDE the plate over the scrim (like the shop's). */
let titleEl: HTMLElement | null = null;
let tickTimer: ReturnType<typeof setInterval> | null = null;

/**
 * How often the Workshop's clocks repaint.
 *
 * One second, because the countdown is shown to the second under a minute. It
 * runs ONLY while the Workshop tab is open — the House and Specialization tabs have
 * nothing that changes on its own, and a timer spinning behind a closed panel
 * is a leak with a rendering cost.
 */
export const CRAFT_TICK_MS = 1_000;

export function createHomeUI(): HTMLElement {
  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.className = 'bv-home-scrim';
  root.hidden = true;

  const panel = document.createElement('div');
  panel.className = 'bv-home panel grained';

  const close = createCloseButton({ label: 'Close home', className: 'bv-home-close', onClick: () => closeHomeUI() });

  const body = document.createElement('div');
  body.className = 'bv-home-body';

  panel.append(close, body);

  // Presentation only: the stage is a column of [title, plate] carrying the
  // soft shadow and the sticker. The title sits above the plate, over the scrim.
  const title = document.createElement('h2');
  title.className = 'bv-home-title';
  title.id = 'home-title';
  title.textContent = 'Home';
  titleEl = title;
  panel.setAttribute('aria-labelledby', 'home-title');
  const stage = document.createElement('div');
  stage.className = 'bv-home-stage';
  stage.append(title, panel);
  root.appendChild(stage);

  // The stage's own margin counts as backdrop, so a tap beside the plate still
  // closes it, exactly as it did when the plate was the scrim's only child.
  root.addEventListener('pointerdown', (e) => {
    if (e.target === root || e.target === stage) closeHomeUI();
  });

  // A modal's own dim area is not `root`, so the rule above never reached it —
  // clicking outside a card did nothing at all, which reads as a frozen panel.
  // Same ladder as Escape, innermost first.
  body.addEventListener('pointerdown', (e) => {
    const target = e.target as HTMLElement | null;
    if (!target || !target.classList.contains('bv-home-modal')) return;
    const state = homeStateStore.get();
    if (state.confirmUpgrade) setUpgradeConfirm(false);
    else if (state.confirmSkipJobId) setSkipConfirm(null);
    else if (state.inspecting) inspectSpecialization(null);
  });

  // One delegated listener rather than a handler per button: the body is
  // rewritten on every store push, so per-element listeners would have to be
  // re-attached (and leaked) on each render.
  body.addEventListener('click', (e) => onBodyClick(e));

  onKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || !isHomeUIOpen()) return;
    e.preventDefault();
    const state = homeStateStore.get();
    // Escape closes the innermost thing first — a modal over the panel, then
    // the panel. Closing both at once loses the player's place.
    if (state.confirmUpgrade) setUpgradeConfirm(false);
    else if (state.confirmSkipJobId) setSkipConfirm(null);
    // The vocation card is a third modal layer, rendered through the same
    // `modal()` helper. Missing it dropped the player out of the whole panel
    // from inside the chooser.
    else if (state.inspecting) inspectSpecialization(null);
    else closeHomeUI();
  };
  document.addEventListener('keydown', onKeyDown);

  preview = new CharacterPreview();
  previewHost = document.createElement('div');
  previewHost.className = 'bv-home-preview';
  preview.mount(previewHost);

  // The material cards count the hideout AND the pack, so any inventory push
  // (a collected craft, a stash, a pickup) re-reads the bill while the panel is
  // open. Compared by reference: the store hands a new sheet on every push.
  let lastSheet = gameStateStore.get().inventory;
  unsubscribers = [homeStateStore.subscribe((state) => {
    if (!isHomeUIOpen()) return;
    render(body, state);
    syncTick(body, state);
  }), gameStateStore.subscribe((game) => {
    if (game.inventory === lastSheet) return;
    lastSheet = game.inventory;
    if (isHomeUIOpen() && homeStateStore.get().home) void refreshHomeView();
  })];

  unregisterPanel?.();
  unregisterPanel = registerPanel({ id: PANEL_ID, isOpen: isHomeUIOpen, close: closeHomeUI });
  return root;
}

/**
 * Is the house the player's own?
 *
 * The deed (`PlayerZone.homeOwned`, granted by `overseer-your-plot` or by the
 * Gold home key) is what turns the building from an abandoned shell into
 * somewhere you can walk into. Null — the store before `loadHome()` has
 * answered — reads as NOT owned, matching the server's own
 * `HOME_GRANT_QUEST_SLUG` rule that the denying failure mode is the safe one.
 */
export function isHomeOwned(): boolean {
  return homeStateStore.get().home?.owned ?? false;
}

export function openHomeUI(): void {
  // The panel refuses to open on someone else's house. `base-view/index.ts`
  // suppresses the building's interactivity and disables the H key and the
  // action-bar button, so this is the third and last line of the same rule —
  // kept because the module that OWNS the panel should be the one that states
  // when it may be shown, rather than trusting every future caller to.
  //
  // Before 2026-09-16 the panel opened for anyone and printed "Not yours yet"
  // above a fully working crafting bench and upgrade button.
  if (!isHomeOwned()) return;
  const el = document.getElementById(ROOT_ID);
  if (!el) return;
  // AFTER both refusals, never before: this panel is the one that can decline
  // to open, and clearing the screen for a window that then does not appear
  // would leave the player looking at nothing.
  closeOtherModals(PANEL_ID);
  el.hidden = false;
  void loadHome().then(() => {
    // Item 236's prompt. Evaluated after the load, because before it there is
    // no home level and no zone state to decide on.
    if (shouldPromptForSpecialization(homeStateStore.get())) setHomeTab('WORKSHOP');
  });
  // Paint once immediately so the panel is never an empty box while the three
  // requests are in flight — the store carries `busy`, and `render` draws it.
  const body = el.querySelector<HTMLElement>('.bv-home-body');
  if (body) render(body, homeStateStore.get());
}

export function isHomeUIOpen(): boolean {
  const el = document.getElementById(ROOT_ID);
  return !!el && !el.hidden;
}

/**
 * Starts or stops the once-a-second repaint, following the Workshop tab.
 *
 * Idempotent in both directions — the store pushes on every write, and
 * restarting the interval on each one would make the clock stutter.
 */
function syncTick(body: HTMLElement, state: HomeState): void {
  const wanted = isHomeUIOpen() && state.tab === 'WORKSHOP' && (state.bench?.jobs.length ?? 0) > 0;
  if (wanted && !tickTimer) {
    tickTimer = setInterval(() => {
      if (!isHomeUIOpen()) { stopTick(); return; }
      render(body, homeStateStore.get());
    }, CRAFT_TICK_MS);
  } else if (!wanted) {
    stopTick();
  }
}

function stopTick(): void {
  if (tickTimer) clearInterval(tickTimer);
  tickTimer = null;
}

export function closeHomeUI(): void {
  const el = document.getElementById(ROOT_ID);
  if (el) el.hidden = true;
  stopTick();
  // Deactivate BEFORE the state reset so the preview's loop is stopped while
  // its host still exists.
  preview?.setActive(false);
  // The panel's own state only: the house stays loaded, or the deed reads as
  // lost and the Home button and front door lock until the next load.
  resetHomePanelState();
}

export function disposeHomeUI(): void {
  stopTick();
  unregisterPanel?.();
  unregisterPanel = null;
  for (const fn of unsubscribers) fn();
  unsubscribers = [];
  if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
  onKeyDown = null;
  preview?.dispose();
  preview = null;
  previewHost = null;
  titleEl = null;
}

// ─── Click routing ──────────────────────────────────────────────────────────

function onBodyClick(e: Event): void {
  const target = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-home-action]');
  if (!target) return;
  const action = target.dataset.homeAction!;
  const value = target.dataset.value ?? '';

  switch (action) {
    case 'change-spec': setChangingSpec(true); break;
    case 'change-spec-cancel': setChangingSpec(false); break;
    case 'tab': setHomeTab(value as HomeState['tab']); break;
    case 'upgrade-prompt': setUpgradeConfirm(true); break;
    case 'upgrade-cancel': setUpgradeConfirm(false); break;
    case 'upgrade-confirm': void upgradeHome(); break;
    case 'skip-prompt': setSkipConfirm(value); break;
    case 'skip-cancel': setSkipConfirm(null); break;
    case 'skip-confirm': void skipCraft(value); break;
    case 'inspect': inspectSpecialization(value || null); break;
    case 'inspect-close': inspectSpecialization(null); break;
    case 'choose': void chooseSpecialization(value); break;
    // The server refuses a switch that would bin uncollected crafts unless the
    // loss is confirmed. This is the re-send.
    case 'choose-confirm': void chooseSpecialization(value, { confirmLoss: true }); break;
    case 'craft': void startCraft(value); break;
    case 'collect': void collectCraft(value); break;
    default: break;
  }
}

// ─── Rendering ──────────────────────────────────────────────────────────────

function render(body: HTMLElement, state: HomeState): void {
  const { home, catalog } = state;
  if (titleEl) titleEl.textContent = home ? `Home — Level ${home.level}` : 'Home';

  body.innerHTML = `
    <div class="bv-home-tabs" role="tablist">
      ${TABS.map((t) => `<button type="button" role="tab" class="bv-home-tab${state.tab === t.id ? ' bv-home-tab-on' : ''}"
        aria-selected="${state.tab === t.id}" data-home-action="tab" data-value="${t.id}">${t.label}</button>`).join('')}
    </div>
    ${state.error ? `<div class="bv-home-error">${esc(state.error)}</div>` : ''}
    ${state.notice ? `<div class="bv-home-notice">${esc(state.notice)}</div>` : ''}
    <div class="bv-home-tabbody">${tabBody(state)}</div>
    ${state.confirmUpgrade && home ? upgradeModal(home) : ''}
    ${state.confirmSkipJobId && state.bench ? skipModal(state, state.confirmSkipJobId) : ''}
  `;

  // The preview is mounted into whichever host this render produced — the
  // House tab has one and the others do not, so the host is genuinely a
  // different element (or absent) after each rewrite.
  mountPreview(body);

  // Nothing above depends on `catalog` being loaded; the Specialization tab renders its
  // own empty state. Referenced so the intent is explicit rather than implied.
  void catalog;
}

/**
 * Attaches the 3D survivor to the House tab's preview box.
 *
 * Two rules, both learned the hard way in `arena-ui.ts` two days ago and both
 * load-bearing:
 *  - the host must be `position: relative` (the canvas is `absolute; inset: 0`,
 *    so an unpositioned host lets it escape and size against the full-screen
 *    scrim). `.bv-home-preview` sets it, and `CharacterPreview.build` now
 *    enforces it too.
 *  - `setActive(true)` must come AFTER the box is laid out. Activating against
 *    a hidden or not-yet-inserted host measures 0x0 and clamps to 1x1.
 */
function mountPreview(body: HTMLElement): void {
  // The House tab is the only one that renders a slot, so its PRESENCE is the
  // condition — a second `tab === 'HOUSE'` check here would be a claim nothing
  // could ever make false, and would drift the day another tab grows a preview.
  const slot = body.querySelector<HTMLElement>('.bv-home-preview-slot');
  if (!slot || !preview || !previewHost) {
    preview?.setActive(false);
    return;
  }
  // MOVED, not rebuilt. The host carries the canvas `CharacterPreview.build()`
  // appended on first activation, and that build never runs again — so a fresh
  // host every render would leave the canvas orphaned in a detached tree.
  slot.replaceWith(previewHost);
  // Last, after the host is in the document and laid out. Activating against a
  // hidden or unattached host measures 0x0 and clamps to 1x1.
  preview.setActive(true);
}

function tabBody(state: HomeState): string {
  if (state.busy && !state.home) return '<div class="bv-home-empty">Walking over…</div>';
  if (!state.home) return '<div class="bv-home-empty">Nothing to show yet.</div>';
  switch (state.tab) {
    case 'WORKSHOP': return workshopTab(state);
    default: return houseTab(state.home);
  }
}

// ─── The House (items 206-208) ──────────────────────────────────────────────

function houseTab(home: HomeView): string {
  const atCap = home.nextLevel === null;
  const span = atCap
    ? `<span class="bv-home-span-now">Level ${home.level}</span><span class="bv-home-span-cap">as good as it gets</span>`
    : `<span class="bv-home-span-now">Level ${home.level}</span>
       <span class="bv-home-span-arrow" aria-hidden="true">→</span>
       <span class="bv-home-span-next">Level ${home.nextLevel}</span>`;

  return `
    <div class="bv-home-house">
      <div class="bv-home-preview-slot"></div>
      <div class="bv-home-bill">
        <h3 class="bv-home-h3">${atCap ? 'The house is finished' : 'What the next floor costs'}</h3>
        ${atCap ? '' : materialCards(home)}
        ${atCap ? '' : gateLines(home)}
      </div>
    </div>
    <div class="bv-home-span">${span}</div>
    <div class="bv-home-actions">
      <button type="button" class="bv-home-upgrade" data-home-action="upgrade-prompt"
        ${home.canUpgrade ? '' : 'disabled'} ${atCap ? 'hidden' : ''}>Upgrade</button>
    </div>
    ${atCap ? '' : stashHint(home)}
  `;
}

/** A check mark for a finished card, stroke-only like the vocation glyphs. */
const CHECK_SVG = '<svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor"'
  + ' stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5 10 17.5 19 7"/></svg>';

/**
 * The next rung's bill as cards — one per material, finished or not.
 *
 * Built from `requirements.materials`, not from `missing`: `missing` drops a
 * line the moment it is covered, and a covered line is exactly the card that
 * has to stay on screen with its check. The count is hideout plus pack
 * (`held + carried`, which the server caps at `needed`), so it climbs as the
 * player gathers, wherever they are holding it. A card that reaches x / x
 * fills, glows and takes a check.
 *
 * Pack units count here but are not spent (the upgrade takes the HIDEOUT
 * only), so a card covered partly from the pack says how many to stash.
 */
function materialCards(home: HomeView): string {
  const req = home.requirements;
  if (!req || req.materials.length === 0) return '<p class="bv-home-note">Nothing needed.</p>';
  const byKey = new Map([...home.missing, ...home.present].map((l) => [l.itemKey, l]));
  const cards = req.materials.map((m) => {
    const line = byKey.get(m.itemKey);
    const name = line?.name ?? m.itemKey;
    const have = Math.min(m.qty, (line?.held ?? 0) + (line?.carried ?? 0));
    const done = have >= m.qty;
    const inPack = line?.carried ?? 0;
    const art = pictureHtml(line?.iconPath ?? null, name, 'bv-home-card-icon', '48px');
    return `<div class="bv-home-card${done ? ' bv-home-card-done' : ''}" data-item-key="${esc(m.itemKey)}">
      ${done ? `<span class="bv-home-card-check" aria-hidden="true">${CHECK_SVG}</span>` : ''}
      <span class="bv-home-card-art">${art}</span>
      <span class="bv-home-card-name">${esc(name)}</span>
      <span class="bv-home-card-qty" aria-label="${have} of ${m.qty}${done ? ', gathered' : ''}">${have} / ${m.qty}</span>
      ${inPack > 0 ? `<span class="bv-home-card-pack">${inPack} in your pack</span>` : ''}
    </div>`;
  }).join('');
  const allDone = req.materials.every((m) => {
    const line = byKey.get(m.itemKey);
    return (line?.held ?? 0) + (line?.carried ?? 0) >= m.qty;
  });
  return `<div class="bv-home-bill-group">
    <h4 class="bv-home-h4 ${allDone ? 'bv-home-h4-done' : 'bv-home-h4-short'}">${allDone ? 'All gathered' : 'Still needed'}</h4>
    <div class="bv-home-cards">${cards}</div>
  </div>`;
}

/**
 * Why Upgrade is still grey when every card is ticked: part of the bill is in
 * the pack, and the build only takes from the hideout.
 */
function stashHint(home: HomeView): string {
  // Keyed, because a part-held line sits in BOTH `missing` and `present`.
  const byKey = new Map([...home.missing, ...home.present].map((l) => [l.itemKey, l]));
  const inPack = [...byKey.values()].reduce((n, l) => n + (l.carried ?? 0), 0);
  if (inPack === 0) return '';
  return `<p class="bv-home-note bv-home-stash">${inPack} of these ${inPack === 1 ? 'is' : 'are'} in your pack. Stash ${inPack === 1 ? 'it' : 'them'} in the hideout to build.</p>`;
}

/**
 * The bill, both halves at once.
 *
 * Item 208 asks for the missing lines AND the present ones on screen together —
 * "show items that are missing + amount and below show items that are
 * present/will be used". `GET /home` returns them pre-split for exactly this,
 * and a line can legitimately appear in both (2 of 6 held is missing 4 and
 * using 2), which is why they are two lists and not one with a flag.
 */
function billLines(home: HomeView): string {
  const missing = home.missing.length
    ? `<div class="bv-home-bill-group">
         <h4 class="bv-home-h4 bv-home-h4-short">Still needed</h4>
         ${home.missing.map((l) => materialRow(l, 'short')).join('')}
       </div>`
    : '';
  const present = home.present.length
    ? `<div class="bv-home-bill-group">
         <h4 class="bv-home-h4">From the hideout</h4>
         ${home.present.map((l) => materialRow(l, 'have')).join('')}
       </div>`
    : '';
  if (!missing && !present) return '<p class="bv-home-note">Nothing needed.</p>';
  return missing + present;
}

/*
 * The Gold "home key" used to be rendered here, as the House tab's own button
 * and confirm modal. It moved to `ui/shop-ui.ts` on 2026-09-16, when this panel
 * stopped opening for players who do not own the house: the House tab was the
 * offer's ONLY surface, so locking the panel would have left a shipped
 * 5 000-Gold purchase with nowhere to be bought. See `deedSection` there.
 *
 * `HomeState.homeKey` / `.confirmHomeKey` and `buyHomeKey()` still live in
 * `state/home-state.ts` — home ownership has one owner on the client and the
 * shop reads it from there rather than keeping a second copy.
 */

/**
 * The "finish it now" confirmation.
 *
 * Shows the price it is about to charge. The number is a PREVIEW computed from
 * the same rounding the server uses (`skipGoldCost`) — the server re-prices at
 * request time and takes no quoted cost, so a craft that crosses an hour
 * boundary between opening this and confirming it charges the lower price. That
 * is the safe direction to be wrong in.
 */
function skipModal(state: HomeState, jobId: string): string {
  const job = state.bench?.jobs.find((j) => j.id === jobId);
  if (!job) return '';
  const cost = skipGoldCost(jobRemainingMs(job, Date.now()), state.bench!.skipGoldPerHour);
  return modal('Finish it now', `
    <p class="bv-home-blurb">Pay <b>${cost} Gold</b> to have
      <b>${esc(job.output.itemKey)}</b> ready this moment, instead of waiting
      ${esc(remaining(jobRemainingMs(job, Date.now())))}.</p>
    <p class="bv-home-note">Priced by the hours left, rounded up — the longer you wait, the less it costs.</p>
  `, [
    { action: 'skip-confirm', label: `Pay ${cost} Gold`, variant: 'go', value: jobId },
    { action: 'skip-cancel', label: 'Keep waiting' },
  ]);
}

function materialRow(line: HomeMaterialLine, kind: 'short' | 'have'): string {
  const art = pictureHtml(line.iconPath, line.name, 'bv-home-mat-icon', '28px');
  return `<div class="bv-home-mat bv-home-mat-${kind}">
    <span class="bv-home-mat-art">${art}</span>
    <span class="bv-home-mat-name">${esc(line.name)}</span>
    <span class="bv-home-mat-count">${line.held} / ${line.needed}</span>
  </div>`;
}

function gateLines(home: HomeView): string {
  const rows: string[] = [];
  const lvl = home.playerLevelGate;
  if (lvl && lvl.required > 0) {
    rows.push(gateRow('Survivor level', lvl.current, lvl.required, lvl.met));
  }
  for (const g of home.attributeGates) {
    rows.push(gateRow(ATTRIBUTE_NAMES[g.code] ?? g.code, g.current, g.required, g.met));
  }
  if (!rows.length) return '';
  return `<div class="bv-home-bill-group">
    <h4 class="bv-home-h4">You also need to be</h4>${rows.join('')}
  </div>`;
}

function gateRow(label: string, current: number, required: number, met: boolean): string {
  return `<div class="bv-home-gate${met ? ' bv-home-gate-met' : ''}">
    <span class="bv-home-gate-name">${esc(label)}</span>
    <span class="bv-home-gate-count">${current} / ${required}</span>
  </div>`;
}

/**
 * The upgrade confirmation (item 208's prompt).
 *
 * Deliberately shows both lists again rather than only the shortfall: a player
 * about to spend six planks and eight nails out of the hideout is entitled to
 * see what is about to leave, and the confirm is the last screen before it
 * does.
 */
function upgradeModal(home: HomeView): string {
  const payable = home.canUpgrade;
  return modal(`Upgrade to level ${home.nextLevel ?? home.level}`, `
    ${home.missing.length ? `<p class="bv-home-note bv-home-note-short">You are short ${home.missing.length} thing(s).</p>` : ''}
    ${billLines(home)}
  `, [
    ...(payable ? [{ action: 'upgrade-confirm', label: 'Build it', variant: 'go' as const }] : []),
    { action: 'upgrade-cancel', label: payable ? 'Not yet' : 'Close' },
  ]);
}

// ─── Specialization — the chooser (item 236), shown inside the Workshop tab ─────────────────

function chooserView(state: HomeState): string {
  const catalog = state.catalog;
  if (!catalog) return '<div class="bv-home-empty">No specializations to show.</div>';

  const zone = catalog.zone;
  const locked = zone.specialization === null;
  const lead = locked
    ? ZONE_LOCK_COPY[zone.reason](zone.requiredLevel)
    : 'Change specialization and you start the new one from nothing. Your house and anything you bought with Gold stay.';

  const inspected = state.inspecting
    ? catalog.options.find((o) => o.key === state.inspecting) ?? null
    : null;

  const back = !locked
    ? '<button type="button" class="bv-home-btn bv-home-back" data-home-action="change-spec-cancel">Back to my workshop</button>'
    : '';
  return `
    ${back}
    <p class="bv-home-lead${locked && zone.reason !== 'NOT_CHOSEN' ? ' bv-home-lead-locked' : ''}">${esc(lead)}</p>
    <div class="bv-home-specs">
      ${catalog.options.map((o) => specTile(o, catalog)).join('')}
    </div>
    ${sharedCrafts(catalog)}
    ${inspected ? specDetail(inspected, catalog) : ''}
  `;
}

/**
 * One chooser tile — "selection is an icon square, below it title".
 *
 * The tile is a BUTTON that opens the detail rather than one that commits: a
 * single click committing to an irreversible-ish choice, from a square whose
 * only label is one word, is not a decision the player got to make. The detail
 * carries the commit.
 *
 * `hoverable` note: the item asks for the detail "on hover". Hover alone
 * cannot work here — the detail is a panel with its own buttons, and a
 * touch device has no hover at all — so the tile shows a short summary on
 * hover via its own CSS, and click opens the full card.
 */
function specTile(option: SpecializationOption, catalog: SpecializationCatalog): string {
  return `<button type="button" class="bv-home-spec${option.active ? ' bv-home-spec-on' : ''}"
      data-home-action="inspect" data-value="${esc(option.key)}"
      aria-pressed="${option.active}">
    ${option.active ? '<span class="bv-home-spec-badge">Yours</span>' : ''}
    <span class="bv-home-spec-icon">${specArtHtml(option.iconKey)}</span>
    <span class="bv-home-spec-name">${esc(option.name)}</span>
    <span class="bv-home-spec-tag">${esc(option.tagline)}</span>
    <span class="bv-home-spec-stats">${craftStats(option, catalog.homeLevel)}</span>
  </button>`;
}

/**
 * What a tile says about its recipes, in the player's words: how many they can make today, and
 * when the rest arrive. "N of M crafts open" said neither - open to what, by when?
 */
export function craftStats(option: SpecializationOption, homeLevel: number): string {
  const total = option.crafts.length;
  if (total === 0) return '<span class="bv-home-spec-stat bv-home-spec-stat-none">No recipes yet</span>';
  const later = option.crafts.filter((c) => c.minHomeLevel > homeLevel);
  const ready = total - later.length;
  const parts = [
    `<span class="bv-home-spec-stat${ready ? ' bv-home-spec-stat-ready' : ''}">${ready} ready to make</span>`,
  ];
  if (later.length) {
    const next = Math.min(...later.map((c) => c.minHomeLevel));
    parts.push(`<span class="bv-home-spec-stat bv-home-spec-stat-later">${later.length} more at house level ${next}</span>`);
  }
  return parts.join('');
}

/** The expanded card: the blurb, the real crafts, and the planned perks. */
function specDetail(
  option: SpecializationOption,
  catalog: SpecializationCatalog,
): string {
  const busy = catalog.zone.specialization !== null
    && 'slotsInUse' in catalog.zone
    && catalog.zone.slotsInUse > 0;
  const switching = catalog.zone.specialization !== null && !option.active;

  const crafts = option.crafts.length
    ? option.crafts.map((c) => {
      const open = c.minHomeLevel <= catalog.homeLevel;
      return `<li class="bv-home-craft${open ? '' : ' bv-home-craft-locked'}">
        <span class="bv-home-craft-name">${esc(c.outputName)}${c.outputQty > 1 ? ` x${c.outputQty}` : ''}</span>
        <span class="bv-home-craft-gate">${open
    ? 'Ready to make'
    : `<img class="bv-home-craft-lock" src="${SPEC_ART_DIR}/locked_128.webp" alt="" draggable="false">House level ${c.minHomeLevel}`}</span>
      </li>`;
    }).join('')
    : '<li class="bv-home-craft bv-home-craft-none">Nothing of its own yet.</li>';

  // Grouped by kind so "Passive / Lets you / Mastery" reads as three claims
  // rather than as one undifferentiated list.
  const perks = ['BUFF', 'CAPABILITY', 'SIGNATURE'].map((kind) => {
    const lines = option.perks.filter((p) => p.kind === kind);
    if (!lines.length) return '';
    return `<div class="bv-home-perk-group">
      <span class="bv-home-perk-kind">${PERK_HEADINGS[kind]}</span>
      <ul class="bv-home-perk-list">${lines.map((p) => `<li>${esc(p.text)}</li>`).join('')}</ul>
    </div>`;
  }).join('');

  const action = option.active
    ? '<span class="bv-home-detail-note">This is your specialization.</span>'
    : `<button type="button" class="bv-home-commit"
         data-home-action="${busy && switching ? 'choose-confirm' : 'choose'}"
         data-value="${esc(option.key)}">
         ${switching ? 'Change specialization' : 'Choose this specialization'}
       </button>`;

  const head = `<header class="bv-home-spec-head">
      <span class="bv-home-spec-hero">${specArtHtml(option.iconKey)}</span>
      <div class="bv-home-spec-headtext">
        <h3 class="bv-home-modal-title">${esc(option.name)}</h3>
        <p class="bv-home-spec-headtag">${esc(option.tagline)}</p>
        ${option.active ? '<span class="bv-home-spec-badge bv-home-spec-badge-inline">Yours</span>' : ''}
      </div>
    </header>`;

  return modal(option.name, `
    <p class="bv-home-blurb">${esc(option.blurb)}</p>
    <h4 class="bv-home-h4">What you can make</h4>
    <ul class="bv-home-crafts">${crafts}</ul>
    ${perks ? `<h4 class="bv-home-h4 bv-home-h4-planned">Coming later — not in the game yet</h4>${perks}` : ''}
    ${busy && switching ? '<p class="bv-home-note bv-home-note-short">Changing specialization throws away whatever is on the bench.</p>' : ''}
  `, [], `<div class="bv-home-detail-foot">${action}
      <button type="button" class="bv-home-btn" data-home-action="inspect-close">Back</button></div>`,
  { cardClass: 'bv-home-modal-card-spec', head });
}

/** The recipes every specialization gets, listed once instead of into all five tiles. */
function sharedCrafts(catalog: SpecializationCatalog): string {
  if (!catalog.shared.length) return '';
  const names = catalog.shared.map((c) => esc(c.outputName)).join(', ');
  return `<div class="bv-home-shared">
    <img class="bv-home-shared-art" src="${SPEC_ART_DIR}/shared_128.webp" alt="" draggable="false">
    <p class="bv-home-shared-text"><span class="bv-home-shared-label">Every specialization can make</span>${names}</p>
  </div>`;
}

// ─── Workshop — the crafting bench (item 237) ───────────────────────────────

function workshopTab(state: HomeState): string {
  const catalog = state.catalog;
  if (!catalog) return '<div class="bv-home-empty">No specializations to show.</div>';
  const chosen = catalog.zone.specialization !== null;
  // Nothing picked yet, or the player asked to change: the chooser. Otherwise the picked
  // specialization as a header with what it gives, and the bench under it.
  if (!chosen || state.changingSpec) return chooserView(state);

  const mine = catalog.options.find((o) => o.active) ?? null;
  const bench = state.bench;
  return `
    ${mine ? mySpecHeader(mine) : ''}
    ${bench ? benchView(bench) : '<div class="bv-home-empty">The bench is not open.</div>'}
  `;
}

/** The chosen specialization: emblem, name, and the boost lines it gives. */
function mySpecHeader(option: SpecializationOption): string {
  const boosts = ['BUFF', 'CAPABILITY', 'SIGNATURE'].map((kind) => {
    const lines = option.perks.filter((p) => p.kind === kind);
    if (!lines.length) return '';
    return `<div class="bv-home-boost">
      <span class="bv-home-boost-kind">${PERK_HEADINGS[kind]}</span>
      <span class="bv-home-boost-text">${lines.map((p) => esc(p.text)).join(' ')}</span>
    </div>`;
  }).join('');
  return `<section class="bv-home-mine">
    <span class="bv-home-spec-hero">${specArtHtml(option.iconKey)}</span>
    <div class="bv-home-mine-main">
      <div class="bv-home-mine-top">
        <div>
          <h3 class="bv-home-mine-name">${esc(option.name)}</h3>
          <p class="bv-home-spec-headtag">${esc(option.tagline)}</p>
        </div>
        <button type="button" class="bv-home-btn" data-home-action="change-spec">Change specialization</button>
      </div>
      ${boosts ? `<div class="bv-home-boosts">${boosts}<p class="bv-home-boost-note">These boosts are not switched on yet. The recipes below are.</p></div>` : ''}
    </div>
  </section>`;
}

function benchView(bench: CraftBench): string {
  return `
    <div class="bv-home-bench-head">
      <span class="bv-home-bench-slots">${bench.slotsInUse} / ${bench.craftSlots} bench${bench.craftSlots === 1 ? '' : 'es'} in use</span>
      <span class="bv-home-bench-level">House level ${bench.homeLevel}</span>
    </div>
    ${helpingHandNote(bench)}
    ${bench.jobs.length ? `<div class="bv-home-jobs">${bench.jobs.map((j) => jobRow(j, Date.now(), bench.skipGoldPerHour)).join('')}</div>` : ''}
    <div class="bv-home-recipes">${bench.recipes.map((r) => recipeRow(r, bench)).join('')}</div>
  `;
}

/**
 * "Helping Hand: crafting 50% faster, Xh Ym left" - empty when none is running.
 * Derived from the instant the server sent and wall time, so it needs no push
 * and is correct after a backgrounded tab; a lapsed value renders nothing.
 */
export function helpingHandNote(bench: CraftBench, now: number = Date.now()): string {
  const until = bench.helpingHandUntil ? Date.parse(bench.helpingHandUntil) : NaN;
  if (Number.isNaN(until) || until <= now) return '';
  return `<p class="bv-home-note bv-home-note-short" data-helping-hand>Helping Hand: crafting 50% faster, ${esc(remaining(until - now))} left</p>`;
}

/**
 * One queued craft.
 *
 * The clock is derived from `readyAt` and wall time, NOT from the `msRemaining`
 * the bench was fetched with. That field is a snapshot: a player watching a
 * ten-minute craft would read "10m" for ten minutes and never see the job
 * become collectable, because nothing on the server ever tells them. And
 * nothing can — `game.gateway.ts` says so explicitly: readiness is LAZY (a
 * stored `readyAt` compared on read), so there is no `craft:ready` push and no
 * moment to emit one at. "The client derives it from the bench payload" is the
 * design, and this is where that happens. `tick()` repaints it once a second
 * while the Workshop is open.
 */
export function jobRemainingMs(job: CraftBench['jobs'][number], now: number): number {
  const readyAt = Date.parse(job.readyAt);
  // An unparseable timestamp falls back to the server's own snapshot rather
  // than to 0 — reporting a craft ready because a date failed to parse would
  // offer a Collect the server then refuses with NOT_READY.
  if (Number.isNaN(readyAt)) return Math.max(0, job.msRemaining);
  return Math.max(0, readyAt - now);
}

function jobRow(job: CraftBench['jobs'][number], now: number, goldPerHour: number): string {
  const left = jobRemainingMs(job, now);
  // `job.ready` is the server's verdict and stays authoritative — the derived
  // clock can only ADD readiness the server has not been asked about yet.
  const ready = job.ready || left <= 0;
  // Never offered on a finished job: `skip()` would charge nothing and report
  // `goldSpent: 0`, so the button would be a no-op that looks like a purchase.
  const action = ready
    ? `<button type="button" class="bv-home-btn bv-home-btn-go" data-home-action="collect" data-value="${esc(job.id)}">Take it</button>`
    : `<button type="button" class="bv-home-btn bv-home-skip" data-home-action="skip-prompt" data-value="${esc(job.id)}">Finish now — ${skipGoldCost(left, goldPerHour)} Gold</button>`;
  return `<div class="bv-home-job${ready ? ' bv-home-job-ready' : ''}">
    <span class="bv-home-job-name">${esc(job.output.qty > 1 ? `${itemLabel(job.output.itemKey)} x${job.output.qty}` : itemLabel(job.output.itemKey))}</span>
    <span class="bv-home-job-time">${ready ? 'Ready' : remaining(left)}</span>
    ${action}
  </div>`;
}

/**
 * One recipe row.
 *
 * A home-level-locked recipe is SHOWN, greyed, with the level that opens it —
 * that is the whole of item 237's promise ("the higher the level of the house,
 * the more recipes can be crafted"), and a recipe the player cannot see is not
 * a reason to haul materials home.
 */
function recipeRow(recipe: CraftRecipeView, bench: CraftBench): string {
  const inputs = recipe.inputs.map((i) => `${i.qty}x ${itemLabel(i.itemKey)}`).join(', ');
  const short = recipe.missing.map((m) => `${itemLabel(m.itemKey)} ${m.held}/${m.needed}`).join(', ');
  const full = bench.slotsInUse >= bench.craftSlots;

  let status = '';
  if (recipe.homeLevelLocked) status = `Needs house level ${recipe.minHomeLevel}`;
  else if (short) status = `Short: ${short}`;
  else if (full) status = 'Every bench is busy';

  return `<div class="bv-home-recipe${recipe.homeLevelLocked ? ' bv-home-recipe-locked' : ''}">
    <div class="bv-home-recipe-main">
      <span class="bv-home-recipe-name">${esc(recipe.outputName)}${recipe.outputQty > 1 ? ` x${recipe.outputQty}` : ''}</span>
      <span class="bv-home-recipe-inputs">${esc(inputs)}${recipe.cashFee > 0 ? ` · ${recipe.cashFee} cash` : ''}</span>
      ${status ? `<span class="bv-home-recipe-status">${esc(status)}</span>` : ''}
    </div>
    <div class="bv-home-recipe-side">
      <span class="bv-home-recipe-time">${remaining(recipe.craftTimeMs)}</span>
      <button type="button" class="bv-home-btn" data-home-action="craft" data-value="${esc(recipe.key)}"
        ${recipe.canCraft && !full ? '' : 'disabled'}>Make</button>
    </div>
  </div>`;
}

// ─── Shared bits ────────────────────────────────────────────────────────────

interface ModalButton { action: string; label: string; variant?: 'go'; value?: string }

/**
 * A card over the panel.
 *
 * Its own layer inside the Home scrim rather than a second full-screen one:
 * stacking another `fixed inset-0` above this panel would put it above
 * `.bv-hud` too on some paths, and the HUD staying readable over every modal is
 * a property this client deliberately has.
 */
function modal(
  title: string, inner: string, buttons: ModalButton[], footer?: string,
  opts: { cardClass?: string; head?: string } = {},
): string {
  const foot = footer ?? `<div class="bv-home-modal-foot">${buttons.map((b) => (
    `<button type="button" class="bv-home-btn${b.variant === 'go' ? ' bv-home-btn-go' : ''}"
       data-home-action="${b.action}"${b.value ? ` data-value="${esc(b.value)}"` : ''}>${esc(b.label)}</button>`
  )).join('')}</div>`;
  return `<div class="bv-home-modal">
    <div class="bv-home-modal-card panel${opts.cardClass ? ` ${opts.cardClass}` : ''}">
      ${opts.head ?? `<h3 class="bv-home-modal-title">${esc(title)}</h3>`}
      <div class="bv-home-modal-body">${inner}</div>
      ${foot}
    </div>
  </div>`;
}

/**
 * A duration as the player reads it.
 *
 * Whole units, largest first, never "0m" — "1h 4m" and "45s", not "1h 04m 00s".
 * Exported for its own test: an off-by-one in the hours/minutes split is
 * invisible on a five-minute craft and wrong on every long one.
 */
/**
 * Gold to finish a craft now.
 *
 * A DUPLICATE of `craftSkipGoldCost` in
 * `backend/src/game/crafting/crafting.types.ts`, and it has to stay one: the
 * server prices the skip at request time and accepts no quoted cost, so this
 * is a PREVIEW, not a quote. The two must round identically or the button lies.
 *
 * Both halves of the rounding are load-bearing. Whole hours rounded UP, because
 * flooring would make the last stretch of every craft free — the exact stretch
 * someone is most likely to pay to skip; and a floor of one hour, so a job
 * seconds from ready can never be skipped for nothing. The result is a step
 * function, so the price falls in visible jumps as the craft runs down. That is
 * the honest shape; do not smooth it.
 */
export function skipGoldCost(msRemaining: number, goldPerHour: number): number {
  const hours = Math.max(1, Math.ceil(Math.max(0, msRemaining) / 3_600_000));
  return Math.max(1, Math.ceil(hours * goldPerHour));
}

export function remaining(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  return `${s}s`;
}

/** `cloth_bundle` -> `Cloth Bundle`. The bench payload carries item keys, not names. */
export function itemLabel(key: string): string {
  return key.split('_').filter(Boolean).map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ');
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}
